import { useQuery } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export const ratePlanFormDataQueryKeys = {
  all: ['rate-plans', 'form-data'] as const,
  new: (vendorId: string | undefined) => [...ratePlanFormDataQueryKeys.all, 'new', vendorId] as const,
  edit: (ratePlanId: string | undefined) => [...ratePlanFormDataQueryKeys.all, 'edit', ratePlanId] as const,
};

export function useNewRatePlanFormData(vendorId: string | undefined) {
  return useQuery({
    queryKey: ratePlanFormDataQueryKeys.new(vendorId),
    queryFn: async () => {
      const [schema, packagedRatePlans] = await Promise.all([
        ReservationService.getRatePlansSchema(),
        ReservationService.getRatePlans(vendorId, true),
      ]);
      return {
        schema: schema.post.body.schema,
        ratePlan: null,
        packagedRatePlans: packagedRatePlans.result,
      };
    },
    enabled: !!vendorId,
  });
}

export function useEditRatePlanFormData(vendorId: string | undefined, ratePlanId: string | undefined) {
  return useQuery({
    queryKey: ratePlanFormDataQueryKeys.edit(ratePlanId),
    queryFn: async () => {
      const [schema, ratePlan, packagedRatePlans] = await Promise.all([
        ReservationService.getRatePlanSchema(ratePlanId),
        ReservationService.getRatePlan(ratePlanId),
        ReservationService.getRatePlans(vendorId, true),
      ]);
      // Exclude itself from the packaged-rate-plan options - a rate plan can't package itself.
      const packagedRatePlansWithoutItself = packagedRatePlans.result.filter(
        (packagedPlan) => packagedPlan.id !== ratePlan.result.id,
      );
      return {
        schema: schema.put.body.schema,
        ratePlan: ratePlan.result,
        packagedRatePlans: packagedRatePlansWithoutItself,
      };
    },
    enabled: !!vendorId && !!ratePlanId,
  });
}
